import {
  ChangeDetectionStrategy,
  Component,
  ErrorHandler,
  Input,
  TemplateRef,
} from '@angular/core';
import { MediaDeviceErrorCode } from '@freelancer/media-devices';
import { HeadingType } from '@freelancer/ui/heading';
import {
  IconBackdrop,
  IconBackdropStyle,
  IconColor,
  IconSize,
} from '@freelancer/ui/icon';
import { TextSize } from '@freelancer/ui/text';
import { FaceFilterType } from '../face-filter';
import { ModalColor } from '../modal/modal-color';
import { ModalRef } from '../modal/modal-ref';

@Component({
  template: `
    <ng-container *flModalTitle>
      {{ modalTitle }}
    </ng-container>

    <div flTrackingSection="AppWebcamVideo">
      <!--
        Custom close button implementation:

        Before, modal has a closeOnBackdropClick feature implemented in D102094. Recent iteration of
        the modal does not include this feature anymore, as it was replaced by the closeable parameter
        in D122185, However, the closeable feature has its limitations. Specifically, either you can
        close the modal or not with no in-between options.

        This approach enables:
        1. Independent control of close button visibility with showCloseButton for video record modal
        2. Consistent styling and behavior with the standard modal implementation's close button
      -->
      <fl-icon
        *ngIf="showCloseButton"
        class="VideoRecordModalCloseButton"
        label="Close Video Record Modal"
        i18n-label="Close Video Record Modal clickable icon"
        [attr.data-color]="color"
        [flHideMobile]="mobileFullscreen"
        [color]="IconColor.FOREGROUND"
        [name]="'ui-close'"
        [backdrop]="IconBackdrop.PRIMARY"
        [backdropStyle]="IconBackdropStyle.TRANSPARENT"
        [hoverAnimation]="'spin-and-fade'"
        [size]="IconSize.SMALL"
        [clickable]="true"
        [id]="'videoRecordModalCloseButton'"
        (click)="closeModal()"
      ></fl-icon>

      <fl-heading
        *ngIf="this.modalHeader"
        class="VideoRecordModal-header"
        [flMarginBottom]="'small'"
        [headingType]="HeadingType.H4"
        [size]="TextSize.MID"
      >
        {{ modalHeader }}
      </fl-heading>

      <div
        *ngIf="this.content"
        class="VideoRecordModal-content"
      >
        <ng-container [ngTemplateOutlet]="content"></ng-container>
      </div>

      <fl-video-record
        [faceFilterType]="faceFilterType"
        [subContent]="subContent"
        [contentWhileRecording]="contentWhileRecording"
        (onError)="onError($event)"
      ></fl-video-record>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./video-record-modal.component.scss'],
})
export class VideoRecordModalComponent {
  HeadingType = HeadingType;
  TextSize = TextSize;
  IconColor = IconColor;
  IconSize = IconSize;
  IconBackdrop = IconBackdrop;
  IconBackdropStyle = IconBackdropStyle;

  @Input() modalHeader?: string;
  @Input() modalTitle?: string;
  @Input() content?: TemplateRef<HTMLElement>;
  @Input() subContent?: TemplateRef<HTMLElement>;
  @Input() contentWhileRecording?: TemplateRef<HTMLElement>;
  @Input() faceFilterType: FaceFilterType = 'none';
  @Input() showCloseButton = true;
  @Input() color = ModalColor.LIGHT;
  @Input() mobileFullscreen = false;

  constructor(
    private errorHandler: ErrorHandler,
    private modalRef: ModalRef<VideoRecordModalComponent>,
  ) {}

  closeModal(): void {
    this.modalRef.close();
  }

  onError(error: MediaDeviceErrorCode): void {
    if (error === MediaDeviceErrorCode.UNSUPPORTED_BROWSER) {
      this.modalRef.setError(
        new Error(
          `The specified MIME type is not supported by the user agent.`,
        ),
      );
    } else {
      const e = new Error(`Unknown error occured: ${error}`);
      this.errorHandler.handleError(e);
      this.modalRef.setError(e);
    }
  }
}
